import { Layout, FloatButton } from 'antd';
import classNames from 'classnames';
import React from 'react';
import Helmet from 'react-helmet';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import useModelProgress from 'components/configurator/models/dashboard/hook/useModelProgress';
import CookiesModal from 'components/cookiesModal';
import PermissionModal from 'components/notifications/PermissionModal';
import useAnalytics from 'config/analytics';

import { getLocaleUsage } from 'constants/LanguageConstants';
import useLocalUserPreferences, { USER_PREFERENCES } from 'hooks/useLocalUserPreferences';
import useScrollTo from 'hooks/useScrollTo';

import { applicationIsReady as applicationIsReadySelector } from 'modules/app/selectors';
import { getLocale } from 'modules/i18n/selectors/data';
import { isOpenPermissionModal as isOpenPermissionModalSelector } from 'modules/notifications/selectors/ui';
import { isReleaseModalOpen as isReleaseModalOpenSelector } from 'modules/releases/selectors/release-modal';
import { getUserCustomBranding, displayImpersonatingBanner } from 'modules/users/selectors/currentUser';

import AppLoader from 'ui/base/FleetbackSplash';

import Content from './Content';
import Header from './header/Header';
import ImpersonateBanner from './ImpersonateBanner';
import * as styles from './MainLayout.module.less';
import NavigationBar from './NavigationBar';

import ReleaseNoteModal from '../../releases/ReleaseNoteModal';

const MainLayout = () => {
  const location = useLocation();
  const { ga } = useAnalytics();

  const impersonate = useSelector(displayImpersonatingBanner);
  const applicationIsReady = useSelector(applicationIsReadySelector);
  const isOpenPermissionModal = useSelector(isOpenPermissionModalSelector);
  const isReleaseModalOpen = useSelector(isReleaseModalOpenSelector);
  const { faviconUrl = 'favicon.ico' } = useSelector(getUserCustomBranding);
  const locale = useSelector(getLocale);

  const { setPreference, [USER_PREFERENCES.COOKIES]: cookiesPreferences } = useLocalUserPreferences();
  useModelProgress();
  useScrollTo();

  const hasCookiesPreferences = Boolean(cookiesPreferences);
  const mustAskPermission = hasCookiesPreferences && isOpenPermissionModal;
  const hasConsent = Boolean(hasCookiesPreferences && cookiesPreferences.ANALYTICS);

  const displayReleaseNote = hasCookiesPreferences && !isOpenPermissionModal && isReleaseModalOpen;

  // Set history length to avoid leaving the app on history.goBack()
  const historyInitialLength = window.history.length;
  global.initialHistoryLength = historyInitialLength;
  global.historyLocalLength = historyInitialLength;

  const handleCloseCookiesModal = React.useCallback(
    newCookiesPreferences => {
      setPreference(USER_PREFERENCES.COOKIES, newCookiesPreferences);
    },
    [setPreference]
  );

  // See https://reactrouter.com/docs/en/v6/api#uselocation
  React.useEffect(() => {
    global.historyLocalLength += 1;
    if (hasConsent) {
      ga.send({ hitType: 'pageview', page: location.pathname }); // Update the user's current page
    }
  }, [location]);

  return (
    <React.Fragment>
      <Helmet htmlAttributes={{ lang: getLocaleUsage(locale).i18n }}>
        <link id="favicon" rel="shortcut icon" href={faviconUrl} />
      </Helmet>
      {!applicationIsReady && <AppLoader />}
      {applicationIsReady && (
        <Layout>
          <Header />
          <Layout hasSider>
            <NavigationBar />
            <Layout.Content className={classNames({ impersonate }, styles.main)} id="main">
              {impersonate && <ImpersonateBanner />}
              <Content />
              <FloatButton.BackTop>
                <div className={styles['fb-back-top']}>
                  <span className={`${styles['fb-back-top__icon']} fb-icon--arrow-double-right`} />
                </div>
              </FloatButton.BackTop>
              {!hasCookiesPreferences && <CookiesModal onClose={handleCloseCookiesModal} />}
              {mustAskPermission && <PermissionModal />}
              {displayReleaseNote && <ReleaseNoteModal />}
            </Layout.Content>
          </Layout>
        </Layout>
      )}
    </React.Fragment>
  );
};

export default MainLayout;
